import React, { useState } from 'react';
import { Button, Input, Select, Alert, Modal } from '@bnvsrnd/brand-assets';
import api from '../../utils/api';
import { trackEvent } from '../../utils/visitorTracker';
import { useNavigate, Link } from 'react-router-dom';
import PolicyAgreement from './PolicyAgreement';
import './SignUp.css';
import logo from '../../assets/images/logo_bk.png';

// 회원 유형 옵션
const roleOptions = [
	{ value: 'instructor', label: '멘토' }
];

// 생년월일(YYYY-MM-DD) → 현재 기준 만 나이. 파싱 불가 시 null.
const calcAge = (birthDate) => {
	if (!birthDate) return null;
	const b = new Date(birthDate);
	if (Number.isNaN(b.getTime())) return null;
	const now = new Date();
	let age = now.getFullYear() - b.getFullYear();
	const md = now.getMonth() - b.getMonth();
	if (md < 0 || (md === 0 && now.getDate() < b.getDate())) age -= 1;
	return age;
};
const INSTRUCTOR_MIN_AGE = 20; // 만 20세 이상만 멘토 가입 가능 (= 만 19세 이하 제한)

const gradeOptions = [
	{ value: '초1', label: '초등학교 1학년' },
	{ value: '초2', label: '초등학교 2학년' },
	{ value: '초3', label: '초등학교 3학년' },
	{ value: '초4', label: '초등학교 4학년' },
	{ value: '초5', label: '초등학교 5학년' },
	{ value: '초6', label: '초등학교 6학년' },
	{ value: '중1', label: '중학교 1학년' },
	{ value: '중2', label: '중학교 2학년' },
	{ value: '중3', label: '중학교 3학년' },
	{ value: '고1', label: '고등학교 1학년' },
	{ value: '고2', label: '고등학교 2학년' },
	{ value: '고3', label: '고등학교 3학년' }
];

function SignUp() {
	const [formData, setFormData] = useState({
		email: '',
		password: '',
		name: '',
		role: '',
		roleInfo: {
			department: '',
			specialization: '',
			staffPosition: '',
			phoneNumber: '',
			birthDate: '',
			address: '',
			organization: '',
			position: ''
		}
	});
	const [error] = useState('');
	const [agreement, setAgreement] = useState({ terms: false, privacy: false });
	const navigate = useNavigate();
	const [showAlertModal, setShowAlertModal] = useState(false);
	const [alertMessage, setAlertMessage] = useState('');
	const [alertType, setAlertType] = useState('');

	const { email, password, confirmPassword } = formData;

	const onChange = e => {
		const { name, value } = e.target;
		if (name.startsWith('roleInfo.')) {
			const field = name.split('.')[1];
			setFormData(prevState => ({
				...prevState,
				roleInfo: {
					...prevState.roleInfo,
					[field]: value
				}
			}));
		} else {
			setFormData(prevState => ({
				...prevState,
				[name]: value
			}));
		}
	};

	// 디자인 시스템 Select는 값(string)을 직접 넘기므로 기존 onChange(event 기반)에 맞춰 합성 이벤트로 변환
	const onSelectChange = (name) => (value) => onChange({ target: { name, value } });

	const validateRole = (role) => {
		const validRoles = ['instructor'];
		if (!validRoles.includes(role)) {
			return '올바른 사용자 유형을 선택해주세요.';
		}
		return '';
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		const roleError = validateRole(formData.role);
		if (roleError) {
			setAlertMessage(roleError);
			setAlertType('error');
			setShowAlertModal(true);
			return;
		}
		try {
			if (password !== confirmPassword) {
				setAlertMessage('비밀번호가 일치하지 않습니다.');
				setAlertType('error');
				setShowAlertModal(true);
				return;
			}

			if (password.length < 6) {
				setAlertMessage('비밀번호는 최소 6자 이상이어야 합니다.');
				setAlertType('error');
				setShowAlertModal(true);
				return;
			}

			if (!formData.name || !formData.email || !formData.roleInfo.phoneNumber) {
				setAlertMessage('이름, 이메일, 전화번호는 필수 입력 항목입니다.');
				setAlertType('error');
				setShowAlertModal(true);
				return;
			}

			if (formData.role === 'instructor' && !formData.roleInfo.address?.trim()) {
				setAlertMessage('주소는 필수 입력 항목입니다.');
				setAlertType('error');
				setShowAlertModal(true);
				return;
			}

			// 만 19세 이하는 멘토 가입 제한 → 학생 로그인 안내
			if (formData.role === 'instructor') {
				const age = calcAge(formData.roleInfo.birthDate);
				if (age === null) {
					setAlertMessage('생년월일을 입력해주세요.');
					setAlertType('error');
					setShowAlertModal(true);
					return;
				}
				if (age < INSTRUCTOR_MIN_AGE) {
					setAlertMessage('만 19세 이하는 멘토 가입이 제한됩니다. 가입 신청 시 학생 로그인을 이용해주세요.');
					setAlertType('error');
					setShowAlertModal(true);
					return;
				}
			}

			if (!agreement.terms || !agreement.privacy) {
				setAlertMessage('이용약관과 개인정보처리방침에 모두 동의해야 회원가입이 가능합니다.');
				setAlertType('error');
				setShowAlertModal(true);
				return;
			}

			const selectedRole = roleOptions.find(option => option.value === formData.role);
			const userData = {
				...formData,
				role: selectedRole.value,
				agreedToTerms: agreement.terms,
				agreedToPrivacy: agreement.privacy
			};

			const response = await api.post('/auth/signup', userData);

			if (response.data.success) {
				trackEvent('signup_success', { role: selectedRole.value });
				setAlertMessage('회원가입이 완료되었습니다. 로그인해주세요.');
				setAlertType('success');
				setShowAlertModal(true);
			}
		} catch (err) {
			setAlertMessage(err.response?.data?.message || '회원가입에 실패했습니다.');
			setAlertType('error');
			setShowAlertModal(true);
		}
	};

	const handleModalConfirm = () => {
		setShowAlertModal(false);
		if (alertType === 'success' || alertType === 'warning') {
			navigate('/login');
		}
	};

	const renderRoleSpecificFields = () => {
		switch (formData.role) {
			case 'learner':
				return (
					<>
						<Input
							label="학교"
							name="roleInfo.organization"
							onChange={onChange}
							placeholder="학교명을 입력하세요"
							value={formData.roleInfo.organization || ''}
						/>
						<Select
							label="학년"
							options={gradeOptions}
							placeholder="학년을 선택하세요"
							value={formData.roleInfo.grade || ''}
							onChange={onSelectChange('roleInfo.grade')}
						/>
					</>
				);
			case 'teacher':
				return (
					<>
						<Input
							label="학교"
							name="roleInfo.organization"
							onChange={onChange}
							placeholder="학교명을 입력하세요"
							value={formData.roleInfo.organization || ''}
						/>
						<Input
							label="담당"
							name="roleInfo.subject"
							onChange={onChange}
							placeholder="담당 과목/업무를 입력하세요"
							value={formData.roleInfo.subject || ''}
						/>
					</>
				);
			case 'instructor':
				return (
					<>
						<Input
							type="date"
							label="생년월일"
							name="roleInfo.birthDate"
							onChange={onChange}
							value={formData.roleInfo.birthDate || ''}
						/>
						<Input
							label="주소 *"
							name="roleInfo.address"
							onChange={onChange}
							placeholder="주소를 입력하세요"
							value={formData.roleInfo.address || ''}
							required
						/>
					</>
				);
			case 'staff':
				return (
					<>
						<Input
							label="소속 부서"
							name="roleInfo.department"
							onChange={onChange}
							placeholder="소속 부서를 입력하세요"
							value={formData.roleInfo.department || ''}
						/>
						<Input
							label="직위"
							name="roleInfo.staffPosition"
							onChange={onChange}
							placeholder="직위를 입력하세요"
							value={formData.roleInfo.staffPosition || ''}
						/>
					</>
				);
			default:
				return null;
		}
	};

	const alertColor =
		alertType === 'success' ? 'var(--status-success)' :
		alertType === 'warning' ? 'var(--status-warning)' : 'var(--status-danger)';
	const alertTitle =
		alertType === 'success' ? '회원가입 완료' :
		alertType === 'warning' ? '주의' : '오류';

	return (
		<div className="signup-container">
			<div className="signup-form-wrapper">
				<div className="signup-logo">
					<img src={logo} alt="BNV Logo" />
				</div>
				<h2 className="signup-title">회원가입</h2>
				{error && <Alert severity="danger" variant="filled" size="sm">{error}</Alert>}
				<form onSubmit={handleSubmit}>
					<div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
						<Select
							options={roleOptions}
							placeholder="회원 유형을 선택하세요"
							value={formData.role}
							onChange={onSelectChange('role')}
						/>

						<Input
							type="text"
							name="name"
							value={formData.name}
							onChange={onChange}
							placeholder="이름"
							required
						/>

						<Input
							type="email"
							name="email"
							value={formData.email}
							onChange={onChange}
							placeholder="이메일"
							required
						/>

						<Input
							type="tel"
							name="roleInfo.phoneNumber"
							value={formData.roleInfo.phoneNumber || ''}
							onChange={onChange}
							placeholder="휴대폰 번호"
							required
						/>

						{renderRoleSpecificFields()}

						<Input
							type="password"
							name="password"
							value={formData.password}
							onChange={onChange}
							placeholder="비밀번호"
							required
						/>

						<Input
							type="password"
							name="confirmPassword"
							value={formData.confirmPassword || ''}
							onChange={onChange}
							placeholder="비밀번호 확인"
							required
						/>

						<PolicyAgreement
							group="instructor"
							checked={agreement}
							onChange={(field, value) => setAgreement(prev => ({ ...prev, [field]: value }))}
						/>

						<Button variant="primary" type="submit" style={{ width: '100%' }}>
							회원가입
						</Button>
					</div>
				</form>

				<div style={{ marginTop: 'var(--space-4)', textAlign: 'center' }}>
					<span>이미 계정이 있으신가요? </span>
					<Link to="/login" className="login-link">로그인</Link>
				</div>
			</div>

			<Modal open={showAlertModal} onClose={() => setShowAlertModal(false)}>
				<Modal.Header>{alertTitle}</Modal.Header>
				<Modal.Body>
					<p style={{ color: alertColor, margin: 0 }}>{alertMessage}</p>
				</Modal.Body>
				<Modal.Footer>
					<Button variant="primary" onClick={handleModalConfirm}>확인</Button>
				</Modal.Footer>
			</Modal>
		</div>
	);
}

export default SignUp;
